import { Session, SessionRuns } from "../types/datamodel";
import { getServerUrl, getLocalStorage } from "../utils";
import { Team, AgentConfig } from "../types/datamodel";
export class SessionAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }

  private getHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getLocalStorage("auth_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async listSessions(userId: string): Promise<Session[]> {
    const response = await fetch(
      `${this.getBaseUrl()}/sessions/?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to fetch sessions");
    return data.data;
  }

  async getSession(sessionId: number, userId: string): Promise<Session> {
    const response = await fetch(
      `${this.getBaseUrl()}/sessions/${sessionId}?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to fetch session");
    return data.data;
  }

  async createSession(
    sessionData: Partial<Session>,
    userId: string
  ): Promise<Session> {
    const session = {
      ...sessionData,
      user_id: userId, // Ensure user_id is included
    };

    const response = await fetch(`${this.getBaseUrl()}/sessions/`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(session),
    });
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to create session");
    return data.data;
  }

  async updateSession(
    sessionId: number,
    sessionData: Partial<Session>,
    userId: string
  ): Promise<Session> {
    const session = {
      ...sessionData,
      id: sessionId,
      user_id: userId, // Ensure user_id is included
    };

    const response = await fetch(
      `${this.getBaseUrl()}/sessions/${sessionId}?user_id=${userId}`,
      {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(session),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to update session");
    return data.data;
  }

  // session runs with messages
  async getSessionRuns(
    sessionId: number,
    userId: string
  ): Promise<SessionRuns> {
    const response = await fetch(
      `${this.getBaseUrl()}/sessions/${sessionId}/runs?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to fetch session runs");
    return data.data; // Returns { runs: RunMessage[] }
  }

  async deleteSession(sessionId: number, userId: string): Promise<void> {
    const response = await fetch(
      `${this.getBaseUrl()}/sessions/${sessionId}?user_id=${userId}`,
      {
        method: "DELETE",
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to delete session");
  }

  // Adding messages endpoint
  async listSessionMessages(sessionId: number, userId: string): Promise<any[]> {
    // Replace 'any' with proper message type
    const response = await fetch(
      `${this.getBaseUrl()}/sessions/${sessionId}/messages?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to fetch messages");
    return data.data;
  }
}

export class TeamAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }

  private getHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getLocalStorage("auth_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async listTeams(userId: string): Promise<Team[]> {
    const response = await fetch(
      `${this.getBaseUrl()}/teams/?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status) throw new Error(data.message || "Failed to fetch teams");
    return data.data;
  }

  async getTeam(teamId: number, userId: string): Promise<Team> {
    const response = await fetch(
      `${this.getBaseUrl()}/teams/${teamId}?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status) throw new Error(data.message || "Failed to fetch team");
    return data.data;
  }

  async createTeam(teamData: Partial<Team>, userId: string): Promise<Team> {
    const team = {
      ...teamData,
      user_id: userId,
    };

    const response = await fetch(`${this.getBaseUrl()}/teams/`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(team),
    });
    const data = await response.json();
    if (!data.status) throw new Error(data.message || "Failed to create team");
    return data.data;
  }

  async deleteTeam(teamId: number, userId: string): Promise<void> {
    const response = await fetch(
      `${this.getBaseUrl()}/teams/${teamId}?user_id=${userId}`,
      {
        method: "DELETE",
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status) throw new Error(data.message || "Failed to delete team");
  }

  // Team-Agent Link Management
  async linkAgent(teamId: number, agentId: number): Promise<void> {
    const response = await fetch(
      `${this.getBaseUrl()}/teams/${teamId}/agents/${agentId}`,
      {
        method: "POST",
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to link agent to team");
  }

  async linkAgentWithSequence(
    teamId: number,
    agentId: number,
    sequenceId: number
  ): Promise<void> {
    const response = await fetch(
      `${this.getBaseUrl()}/teams/${teamId}/agents/${agentId}/${sequenceId}`,
      {
        method: "POST",
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(
        data.message || "Failed to link agent to team with sequence"
      );
  }

  async unlinkAgent(teamId: number, agentId: number): Promise<void> {
    const response = await fetch(
      `${this.getBaseUrl()}/teams/${teamId}/agents/${agentId}`,
      {
        method: "DELETE",
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to unlink agent from team");
  }

  async getTeamAgents(teamId: number): Promise<AgentConfig[]> {
    const response = await fetch(
      `${this.getBaseUrl()}/teams/${teamId}/agents`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to fetch team agents");
    return data.data;
  }
}

export class PlanAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }

  private getHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getLocalStorage("auth_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async listPlans(userId: string): Promise<any[]> {
    const response = await fetch(
      `${this.getBaseUrl()}/plans/?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status) throw new Error(data.message || "Failed to fetch plans");
    return data.data;
  }

  async getPlan(planId: number, userId: string): Promise<any> {
    const response = await fetch(
      `${this.getBaseUrl()}/plans/${planId}?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status) throw new Error(data.message || "Failed to fetch plan");
    return data.data;
  }

  async createPlan(planData: Partial<any>, userId: string): Promise<any> {
    const plan = {
      ...planData,
      user_id: userId,
    };

    const response = await fetch(`${this.getBaseUrl()}/plans/`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(plan),
    });
    const data = await response.json();
    if (!data.status) throw new Error(data.message || "Failed to create plan");
    return data.data;
  }

  async updatePlan(
    planId: number,
    planData: Partial<any>,
    userId: string
  ): Promise<any> {
    if (!planData.task) {
      console.error("Missing task in planData:", planData);
    }
    if (!planData.steps || !Array.isArray(planData.steps)) {
      console.error("Missing or invalid steps in planData:", planData);
    }

    const { created_at, ...dataWithoutCreatedAt } = planData;

    const plan = {
      ...dataWithoutCreatedAt,
      id: planId,
      user_id: userId,
      updated_at: null, // This will be replaced by the server with current time
    };

    try {
      const response = await fetch(
        `${this.getBaseUrl()}/plans/${planId}?user_id=${userId}`,
        {
          method: "PUT",
          headers: this.getHeaders(),
          body: JSON.stringify(plan),
        }
      );

      const data = await response.json();
      if (!data.status)
        throw new Error(data.message || "Failed to update plan");
      return data.data;
    } catch (error) {
      console.error("Error in updatePlan:", error);
      throw error;
    }
  }

  async deletePlan(planId: number, userId: string): Promise<void> {
    try {
      const response = await fetch(
        `${this.getBaseUrl()}/plans/${planId}?user_id=${userId}`,
        {
          method: "DELETE",
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to delete plan. Server responded with status: ${response.status}`
        );
      }

      const data = await response.json();

      if (!data.status) {
        throw new Error(data.message || "Failed to delete plan");
      }
    } catch (error) {
      throw error;
    }
  }

  async learnPlan(sessionId: number, userId: string): Promise<any> {
    try {
      const response = await fetch(`${this.getBaseUrl()}/plans/learn_plan`, {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        // Log the complete error response
        const errorText = await response.text();
        console.error("Full error response:", errorText);
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.detail || response.statusText);
        } catch (e) {
          throw new Error(
            `${response.status} ${response.statusText}: ${errorText}`
          );
        }
      }

      return await response.json();
    } catch (error) {
      console.error("Error learning plan:", error);
      throw error;
    }
  }
}

export class SettingsAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }

  private getHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getLocalStorage("auth_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async getSettings(userId: string): Promise<Record<string, any>> {
    const response = await fetch(
      `${this.getBaseUrl()}/settings/?user_id=${userId}`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to fetch settings");
    return data.data.config || {}; // Return just the config object
  }

  async updateSettings(
    userId: string,
    config: Record<string, any>
  ): Promise<void> {
    const response = await fetch(`${this.getBaseUrl()}/settings/`, {
      method: "PUT",
      headers: this.getHeaders(),
      body: JSON.stringify({
        user_id: userId,
        config: config,
      }),
    });
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to update settings");
  }

  async getConfigInfo(): Promise<{ has_config_file: boolean; config_file_path: string | null; config_content: any }> {
    const response = await fetch(
      `${this.getBaseUrl()}/settings/config-info`,
      {
        headers: this.getHeaders(),
      }
    );
    const data = await response.json();
    if (!data.status)
      throw new Error(data.message || "Failed to fetch config info");
    return data.data;
  }
}

export class McpAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }

  private getHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getLocalStorage("auth_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async testMcpServer(serverConfig: any): Promise<any> {
    const response = await fetch(`${this.getBaseUrl()}/mcp/test-mcp-server`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({ server_config: serverConfig }),
    });
    const data = await response.json();
    // The MCP endpoint returns the response directly without status/data wrapper
    return data;
  }
}

export interface AuthUser {
  id: number;
  email: string;
  name: string | null;
  created_at: string;
  is_active: boolean;
  is_approved: boolean;
  is_admin: boolean;
  has_gitlab: boolean;
  has_pipedrive: boolean;
  has_microsoft: boolean;
}

export interface AuthResponse {
  status: boolean;
  message: string;
  data: {
    user: AuthUser;
    token: {
      access_token: string;
      token_type: string;
    };
  };
}

export class AuthAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }

  private getHeaders(): HeadersInit {
    return {
      "Content-Type": "application/json",
    };
  }

  async signup(email: string, password: string, name?: string): Promise<AuthResponse> {
    const response = await fetch(`${this.getBaseUrl()}/auth/signup`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({ email, password, name }),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Signup failed");
    }
    return data;
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${this.getBaseUrl()}/auth/login`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Login failed");
    }
    return data;
  }

  async logout(token: string): Promise<void> {
    await fetch(`${this.getBaseUrl()}/auth/logout`, {
      method: "POST",
      headers: {
        ...this.getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getMe(token: string): Promise<AuthUser> {
    const response = await fetch(`${this.getBaseUrl()}/auth/me`, {
      headers: {
        ...this.getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to get user");
    }
    return data.data;
  }

  // Admin methods
  async listUsers(token: string): Promise<AuthUser[]> {
    const response = await fetch(`${this.getBaseUrl()}/auth/admin/users`, {
      headers: {
        ...this.getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to list users");
    }
    return data.data;
  }

  async approveUser(
    token: string,
    userId: number,
    approved: boolean,
    hasGitlab?: boolean,
    hasPipedrive?: boolean,
  ): Promise<AuthUser> {
    const body: any = { user_id: userId, approved };
    if (hasGitlab !== undefined) body.has_gitlab = hasGitlab;
    if (hasPipedrive !== undefined) body.has_pipedrive = hasPipedrive;
    const response = await fetch(`${this.getBaseUrl()}/auth/admin/approve`, {
      method: "POST",
      headers: {
        ...this.getHeaders(),
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to update user");
    }
    return data.data;
  }

  async updateUserServices(
    token: string,
    userId: number,
    services: { has_gitlab?: boolean; has_pipedrive?: boolean; has_microsoft?: boolean },
  ): Promise<AuthUser> {
    const response = await fetch(`${this.getBaseUrl()}/auth/admin/user-services/${userId}`, {
      method: "PATCH",
      headers: {
        ...this.getHeaders(),
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(services),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to update user services");
    }
    return data.data;
  }

  // Microsoft Outlook connection
  async microsoftDeviceCode(token: string): Promise<{ user_code: string; verification_uri: string; expires_in: number; already_connected?: boolean }> {
    const response = await fetch(`${this.getBaseUrl()}/auth/microsoft/device-code`, {
      method: "POST",
      headers: { ...this.getHeaders(), Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!data.status) throw new Error(data.detail || data.message || "Failed to initiate Microsoft auth");
    return data.data;
  }

  async microsoftStatus(token: string): Promise<{ connected: boolean; pending: boolean }> {
    const response = await fetch(`${this.getBaseUrl()}/auth/microsoft/status`, {
      headers: { ...this.getHeaders(), Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!data.status) throw new Error(data.detail || data.message || "Failed to check status");
    return data.data;
  }

  async microsoftDisconnect(token: string): Promise<void> {
    const response = await fetch(`${this.getBaseUrl()}/auth/microsoft/disconnect`, {
      method: "POST",
      headers: { ...this.getHeaders(), Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!data.status) throw new Error(data.detail || data.message || "Failed to disconnect");
  }

  async deleteUser(token: string, userId: number): Promise<void> {
    const response = await fetch(`${this.getBaseUrl()}/auth/admin/users/${userId}`, {
      method: "DELETE",
      headers: {
        ...this.getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to delete user");
    }
  }
}

// Sharing types
export interface ShareInfo {
  share_token: string;
  share_url: string;
  expires_at: string;
  expires_in_days: number;
}

export interface SharedSessionData {
  session: any;
  runs: any[];
  owner_name: string;
  owner_email: string;
  can_fork: boolean;
  expires_at: string;
}

export interface ShareListItem {
  session_id: number;
  session_name: string;
  share_token: string;
  share_url: string;
  expires_at: string;
  is_active: boolean;
}

export class SharingAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }

  private getHeaders(token: string): HeadersInit {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  async createShare(token: string, sessionId: number): Promise<ShareInfo> {
    const response = await fetch(`${this.getBaseUrl()}/sessions/${sessionId}/share`, {
      method: "POST",
      headers: this.getHeaders(token),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to create share link");
    }
    return data.data;
  }

  async getSharedSession(token: string, shareToken: string): Promise<SharedSessionData> {
    const response = await fetch(`${this.getBaseUrl()}/shared/${shareToken}`, {
      headers: this.getHeaders(token),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to get shared session");
    }
    return data.data;
  }

  async forkSession(token: string, shareToken: string): Promise<{ new_session_id: number; message: string }> {
    const response = await fetch(`${this.getBaseUrl()}/shared/${shareToken}/fork`, {
      method: "POST",
      headers: this.getHeaders(token),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to fork session");
    }
    return data.data;
  }

  async revokeShare(token: string, sessionId: number): Promise<void> {
    const response = await fetch(`${this.getBaseUrl()}/sessions/${sessionId}/share`, {
      method: "DELETE",
      headers: this.getHeaders(token),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to revoke share");
    }
  }

  async listMyShares(token: string): Promise<ShareListItem[]> {
    const response = await fetch(`${this.getBaseUrl()}/sessions/shared`, {
      headers: this.getHeaders(token),
    });
    const data = await response.json();
    if (!data.status) {
      throw new Error(data.detail || data.message || "Failed to list shares");
    }
    return data.data;
  }
}

// Briefing types
export interface TeamMemberStat {
  user_id: number;
  name: string;
  email: string;
  calls: number;
  emails: number;
  meetings: number;
  tasks: number;
  other: number;
  done: number;
  total: number;
  done_rate: number;
  open_deals: number;
  open_value: number;
  won_deals: number;
  won_value: number;
  lost_deals: number;
}

export interface BriefingData {
  status: boolean;
  data: {
    fetched_at: string;
    configured: boolean;
    gitlab: {
      open_mrs?: { data?: any[]; error?: string };
      open_issues?: { data?: any[]; error?: string };
      error?: string;
    } | null;
    pipedrive: {
      activities?: { data?: any[]; error?: string };
      open_deals?: { data?: any[]; error?: string };
      error?: string;
    } | null;
    team_performance?: {
      data?: TeamMemberStat[];
      active_count?: number;
      total_users?: number;
      window_days?: number;
      error?: string;
    } | null;
  };
}

export class BriefingAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }

  private getHeaders(): HeadersInit {
    const headers: Record<string, string> = {};
    const token = getLocalStorage("auth_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async fetchBriefing(force = false): Promise<BriefingData> {
    const url = force
      ? `${this.getBaseUrl()}/briefing/?force=true`
      : `${this.getBaseUrl()}/briefing/`;
    const response = await fetch(url, {
      headers: this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Briefing fetch failed: ${response.status}`);
    }
    return response.json();
  }
}

export const authAPI = new AuthAPI();
export const teamAPI = new TeamAPI();
export const sessionAPI = new SessionAPI();
export const planAPI = new PlanAPI();
export const settingsAPI = new SettingsAPI();
export const mcpAPI = new McpAPI();
export const sharingAPI = new SharingAPI();
export const briefingAPI = new BriefingAPI();
// ============================================================
// Bug Reports
// ============================================================
export interface BugReport {
  id: number;
  user_id: number;
  text: string;
  screenshot_path?: string | null;
  severity: "low" | "medium" | "high";
  status: "pending" | "triaged" | "fixing" | "pr_open" | "merged" | "deployed" | "failed";
  branch_name?: string | null;
  pr_url?: string | null;
  diagnosis?: string | null;
  suggested_fix?: string | null;
  github_issue_number?: number | null;
  github_issue_url?: string | null;
  created_at: string;
  updated_at?: string | null;
  fixed_at?: string | null;
}

export class BugAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }
  private authHeader(token: string): Record<string, string> {
    return { Authorization: `Bearer ${token}` };
  }

  async createBug(
    token: string,
    payload: { text: string; severity: "low" | "medium" | "high"; screenshot?: File }
  ): Promise<BugReport> {
    const fd = new FormData();
    fd.append("text", payload.text);
    fd.append("severity", payload.severity);
    if (payload.screenshot) fd.append("screenshot", payload.screenshot);
    const res = await fetch(`${this.getBaseUrl()}/bugs`, {
      method: "POST",
      headers: this.authHeader(token),
      body: fd,
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`Failed to submit bug (${res.status}): ${t}`);
    }
    return res.json();
  }

  async listMyBugs(token: string): Promise<BugReport[]> {
    const res = await fetch(`${this.getBaseUrl()}/bugs/mine`, {
      headers: this.authHeader(token),
    });
    if (!res.ok) throw new Error(`Failed to load bugs (${res.status})`);
    return res.json();
  }

  async listAllBugs(token: string, statusFilter?: string): Promise<BugReport[]> {
    const qs = statusFilter ? `?status_filter=${encodeURIComponent(statusFilter)}` : "";
    const res = await fetch(`${this.getBaseUrl()}/bugs${qs}`, {
      headers: this.authHeader(token),
    });
    if (!res.ok) throw new Error(`Failed to load bugs (${res.status})`);
    return res.json();
  }

  async patchBug(
    token: string,
    id: number,
    patch: Partial<Pick<BugReport, "status" | "severity" | "diagnosis" | "suggested_fix" | "branch_name" | "pr_url">>
  ): Promise<BugReport> {
    const res = await fetch(`${this.getBaseUrl()}/bugs/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...this.authHeader(token) },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`Failed to update bug (${res.status})`);
    return res.json();
  }
}

export const bugAPI = new BugAPI();


// ── Müllkalender Studio API ─────────────────────────────────────────────
//
// Polling-based (no WebSocket) — see backend/web/routes/muellkalender.py
// for the rationale. The Studio's whole point is to be immune to the
// disconnect/timeout class of bugs that the chat flow was hitting.

/** Plain-German self-diagnosis of a result — "Woran liegt's? Du / die Quelle /
 * die App" + concrete next steps. Computed server-side (routes/muellkalender.py
 * JobOut._diagnosis → muellkalender/diagnosis.py) so chat + Studio agree.
 * Rendered by ErgebnisDiagnose.tsx. */
export interface MuellkalenderDiagnosis {
  category:
    | "success"
    | "partial"
    | "empty"
    | "unknown_gemeinde"
    | "unverified"
    | "error";
  culprit: "none" | "source" | "user" | "app";
  culprit_label: string;
  tone: "success" | "info" | "warning" | "error";
  headline: string;
  explanation: string;
  notes: string[];
  actions: { label: string; action: string }[];
}

export interface MuellkalenderJob {
  id: number;
  user_id: number;
  gemeinde: string;
  year: number;
  schema_variant: "wide_ino" | "long_thomas";
  status: "pending" | "running" | "done" | "failed";
  xlsx_filename?: string | null;
  event_count?: number | null;
  parser_used?: string | null;
  warnings?: string[] | null;
  error_message?: string | null;
  postal_code?: string | null;
  street?: string | null;
  house_number?: string | null;
  /** Confidence score in [0.0, 1.0]. Higher = more trust the
   * extraction is correct. Drives the Vollautomatisch threshold:
   *   ≥ 0.90 strict  (only thomas_baked + clean cascades)
   *   ≥ 0.70 balanced
   *   ≥ 0.55 permissive
   *   < 0.55 low      (flag for review)
   * null = not yet finished or pre-scoring row (legacy job).
   */
  confidence_score?: number | null;
  created_at: string;
  updated_at?: string | null;
  diagnosis?: MuellkalenderDiagnosis | null;
}

export interface RegistryEntry {
  name: string;
  canonical_name: string;
  type: "html_table" | "pdf_yearly" | "ical" | "web_form_address" | "api";
  confidence: "low" | "medium" | "high";
  needs_address: boolean;
  primary_url: string;
  postal_code?: string | null;
  bundesland?: string | null;
  /** Set to "<slug>_baked" for the 7 Gemeinden whose 2026 schedule
   * is bundled in the backend — frontend renders a "Verifiziert"
   * badge + gold map-pin for these. */
  parser_hint?: string | null;
}

export interface RefreshAllResult {
  year: number;
  queued: number;
  skipped_unknown: number;
  detail: MuellkalenderJob[];
}

export class MuellkalenderStudioAPI {
  private getBaseUrl(): string {
    return getServerUrl();
  }
  private authHeader(token: string): Record<string, string> {
    return { Authorization: `Bearer ${token}` };
  }
  private async parseOrThrow<T>(res: Response, op: string): Promise<T> {
    if (!res.ok) {
      let detail = "";
      try {
        detail = (await res.json())?.detail || (await res.text());
      } catch {
        detail = res.statusText;
      }
      throw new Error(`${op} failed (${res.status}): ${detail}`);
    }
    return res.json() as Promise<T>;
  }

  /** Quick "should I render the header link?" check. Returns
   *  ``{allowed: false}`` for both anonymous and non-allowlisted
   *  callers, so the caller doesn't need a try/catch. */
  async checkAccess(
    token: string | null | undefined
  ): Promise<{ allowed: boolean }> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${this.getBaseUrl()}/muellkalender/access`, {
      headers,
    });
    if (!res.ok) return { allowed: false };
    return res.json();
  }

  async listRegistry(token: string): Promise<RegistryEntry[]> {
    const res = await fetch(`${this.getBaseUrl()}/muellkalender/registry`, {
      headers: this.authHeader(token),
    });
    return this.parseOrThrow<RegistryEntry[]>(res, "load registry");
  }

  /** Slugs from Thomas's hand-curated library — drives the gold-pin
   *  verified layer on the Austria map. Backend reads the local
   *  ``data/muellkalender/thomas_baked/*.json`` directory. */
  async listThomasVerified(
    token: string,
  ): Promise<{ count: number; slugs: string[] }> {
    const res = await fetch(
      `${this.getBaseUrl()}/muellkalender/thomas-verified`,
      { headers: this.authHeader(token) },
    );
    return this.parseOrThrow<{ count: number; slugs: string[] }>(
      res,
      "load thomas-verified",
    );
  }

  async createJob(
    token: string,
    payload: {
      gemeinde: string;
      year: number;
      schema_variant?: "wide_ino" | "long_thomas";
      postal_code?: string;
      street?: string;
      house_number?: string;
      // Magic-Extract path: Gemini plans which URLs/queries to try
      // before the cascade runs. Server-side identical lifecycle.
      agentic?: boolean;
    }
  ): Promise<MuellkalenderJob> {
    const res = await fetch(`${this.getBaseUrl()}/muellkalender/jobs`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...this.authHeader(token) },
      body: JSON.stringify(payload),
    });
    return this.parseOrThrow<MuellkalenderJob>(res, "submit job");
  }

  async listJobs(token: string): Promise<MuellkalenderJob[]> {
    const res = await fetch(`${this.getBaseUrl()}/muellkalender/jobs`, {
      headers: this.authHeader(token),
    });
    return this.parseOrThrow<MuellkalenderJob[]>(res, "list jobs");
  }

  async getJob(token: string, id: number): Promise<MuellkalenderJob> {
    const res = await fetch(`${this.getBaseUrl()}/muellkalender/jobs/${id}`, {
      headers: this.authHeader(token),
    });
    return this.parseOrThrow<MuellkalenderJob>(res, "load job");
  }

  async deleteJob(token: string, id: number): Promise<void> {
    const res = await fetch(`${this.getBaseUrl()}/muellkalender/jobs/${id}`, {
      method: "DELETE",
      headers: this.authHeader(token),
    });
    if (!res.ok) {
      throw new Error(`delete job failed (${res.status})`);
    }
  }

  /** Returns a URL the browser can directly use as a download link.
   *  The Authorization header CAN'T be applied to a plain <a href>, so
   *  callers should use fetch + blob if they want the auth header.
   *  ``downloadXlsxBlob`` below does that. */
  xlsxUrl(id: number): string {
    return `${this.getBaseUrl()}/muellkalender/jobs/${id}/xlsx`;
  }

  async downloadXlsxBlob(token: string, id: number): Promise<Blob> {
    const res = await fetch(this.xlsxUrl(id), {
      headers: this.authHeader(token),
    });
    if (!res.ok) {
      throw new Error(`download failed (${res.status})`);
    }
    return res.blob();
  }

  async refreshAll(
    token: string,
    year: number
  ): Promise<RefreshAllResult> {
    const res = await fetch(
      `${this.getBaseUrl()}/muellkalender/refresh-all?year=${year}`,
      {
        method: "POST",
        headers: this.authHeader(token),
      }
    );
    return this.parseOrThrow<RefreshAllResult>(res, "refresh-all");
  }

  /** Submit a job from a user-uploaded PDF (bypasses the cascade /
   *  registry). Used when Thomas has the official Abfuhrkalender PDF
   *  on disk for a Gemeinde that isn't in our curated set yet — most
   *  Salzburg + Steiermark Gemeinden are this case. */
  async createJobFromPdf(
    token: string,
    payload: {
      file: File;
      gemeinde: string;
      year: number;
      schema_variant?: "wide_ino" | "long_thomas";
      postal_code?: string;
    }
  ): Promise<MuellkalenderJob> {
    const fd = new FormData();
    fd.append("file", payload.file);
    fd.append("gemeinde", payload.gemeinde);
    fd.append("year", String(payload.year));
    if (payload.schema_variant) {
      fd.append("schema_variant", payload.schema_variant);
    }
    if (payload.postal_code) {
      fd.append("postal_code", payload.postal_code);
    }
    const res = await fetch(
      `${this.getBaseUrl()}/muellkalender/jobs/from-pdf`,
      {
        method: "POST",
        // NOTE: do NOT set Content-Type — fetch picks the multipart
        // boundary automatically when body is FormData.
        headers: this.authHeader(token),
        body: fd,
      }
    );
    return this.parseOrThrow<MuellkalenderJob>(res, "submit PDF job");
  }

  /** Hunt for candidate source URLs for a Gemeinde via Tavily web
   *  search. Saves Thomas the manual "google for the official
   *  Gemeinde website" step before submitting a job. */
  async huntSource(
    token: string,
    gemeinde: string,
    year: number,
    plz?: string
  ): Promise<{
    gemeinde: string;
    year: number;
    query: string;
    candidates: Array<{ url: string; title?: string; snippet?: string }>;
  }> {
    const params = new URLSearchParams({
      gemeinde: gemeinde,
      year: String(year),
    });
    if (plz) params.set("plz", plz);
    const res = await fetch(
      `${this.getBaseUrl()}/muellkalender/source-hunt?${params}`,
      { headers: this.authHeader(token) }
    );
    return this.parseOrThrow(res, "source hunt");
  }
}

export const muellkalenderAPI = new MuellkalenderStudioAPI();

